import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApplicationStateService } from './application-state.service';
import { AuthRequestInitialService } from './auth-request-initial.service';
import { API_URL, MEMBER_REPRESENTATIVE, OTHER } from '@core/constants';
import {
  IServiceLocation,
  IAuthorizationDTO,
  IAuthorizationSummaryDTO,
  ICDCode,
  IMemberAddress,
  IMemberDTO,
  IServiceLocationDTO,
  IAuthorizationCreateDTO,
} from '@core/model';
import { AddressType } from '@core/enumerations';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  constructor(
    private http: HttpClient,
    private applicationStateService: ApplicationStateService,
    private authRequestInitialService: AuthRequestInitialService,
    private authenticationService: AuthenticationService,
    @Inject(API_URL) private apiUrl: string
  ) { }

  getAuthorization(authorizationByProductId: string): Observable<IAuthorizationDTO> {
    return this.http.get<IAuthorizationDTO>(`${this.getBaseUrl}/authorizations/${authorizationByProductId}`);
  }

  getMemberByAuthNumber(authNumber: string): Observable<IMemberDTO> {
    return this.http.get<IMemberDTO>(`${this.getBaseUrl}/authorizations/${authNumber}/member`);
  }

  create(): Observable<IAuthorizationCreateDTO> {
    const { memberDTO } = this.applicationStateService.member$.value;
    const { references, type } = this.authRequestInitialService.authorizationData$.value;

    if (references.referralSource?.applicationKey !== MEMBER_REPRESENTATIVE &&
      references.referralSourceMemberRepresentative !== null) {
      references.referralSourceMemberRepresentative = null;
    }

    const serviceLocation = this.getAddressAndLocationDate();

    const body = {
      authorization: {
        authorizationByProduct: {
          memberId: memberDTO.memberId,
          requestReceivedDate: references.requestReceivedDate
        },
        type,
        urgency: references.urgency,
        referralSource: references.referralSource,
        referralSourceMemberRepresentative: references.referralSourceMemberRepresentative,
        requestSource: references.requestSource,
      },
      serviceLocation,
    };

    return this.http.post<IAuthorizationCreateDTO>(`${this.getBaseUrl}/authorizations`, body);
  }

  update(
    authorizationByProductId: string,
    isExternal: boolean,
    authDataForUpdating = null
  ): Observable<IAuthorizationDTO> {
    const body = authDataForUpdating || this.getAuthDataForUpdating(authorizationByProductId, isExternal);

    return this.http.put<IAuthorizationDTO>(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}`,
      body
    );
  }

  activate(authorizationByProductId: string): Observable<IAuthorizationDTO> {
    return this.http.put<IAuthorizationDTO>(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}/activate`, {}
    );
  }

  delete(authorizationByProductId: string): Observable<unknown> {
    return this.http.delete(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}`
    );
  }

  /**
   * Submit Incomplete authorization
   * @description Permission: DMEPOSAuthorization.Update
   */
  submit(authorizationByProductId: string): Observable<string> {
    return this.http.put<string>(`${this.getBaseUrl}/authorizations/${authorizationByProductId}/submit`, {
      authorizationByProductId,
    })
  }

  /**
   * Submit Initial authorization
   * @param authorizationByProductId
   */
  submitInitial(authorizationByProductId: string, providerComment: string): Observable<string> {
    const { contact } = this.authRequestInitialService.authorizationData$.value;
    const servicingProvider = this.authRequestInitialService.newServicingProvider$.value;
    const physician = this.authRequestInitialService.newPhysician$.value;
    const isAddNewPhysician = this.authRequestInitialService.isAddNewPhysician$.value;
    const originalDocuments = this.authRequestInitialService.documents$.value;

    // Since we perform a Doc Upload a little differently for Internal v External users
    // We need to send this extra data along with Internal & External requests for IFA integration
    // Im sure there's a better way to do this, but this should suffice for now.
    const documents = originalDocuments?.map((doc) => ({
      description: doc.description,
      tenantId: this.applicationStateService.tenantId,
      documentSource: doc.documentSource,
      fileContent: doc.fileContent,
      documentTypes: doc.documentTypes,
      documentFileName: doc.documentFileName,
      inboundFaxId: doc.inboundFaxId
    }));

    return this.http.put<string>(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}/submit`,
      {
        authorizationByProductId,
        immediateContactDetails: {
          name: contact?.contactName,
          fax: contact?.contactFax,
          phone: contact?.contactPhone,
          phoneExtension: contact?.contactPhoneExtension
        },
        documents,
        servicingProviderContactInfo: servicingProvider
          ? {
              npi: servicingProvider.npi,
              providerName: servicingProvider.providerName,
              phone: servicingProvider.phone,
              fax: servicingProvider.fax,
              address: servicingProvider.street,
              city: servicingProvider.city,
              state: servicingProvider.state,
              zipCode: servicingProvider.zip,
        } : null,
        orderingProviderContactInfo: physician || isAddNewPhysician ? {
                firstName: physician.firstName,
                lastName: physician.lastName,
                practiceNpiNumber: physician.practiceNpinumber,
                physicalStreet: physician.physicalStreet,
                physicalCity: physician.physicalCity,
                physicalState: physician.physicalState,
                physicalZip: physician.physicalZip,
                phone: physician.phone,
        } : null,
        providerComment
      }
    );
  }

  saveDraft(): Observable<string> {
    const { type, references, contact } = this.authRequestInitialService.authorizationData$.value;
    const diagnosis = this.authRequestInitialService.selectedIcdCode$.value?.map((diagnose: ICDCode, index: number) => ({
          icdCodeId: diagnose?.icdCodeID,
          icdOrderType: !index ? 'Primary' : null,
          icdCode: {
            code: diagnose.code,
            description: diagnose?.description,
            isActive: diagnose?.isActive,
            pdgm: diagnose?.pdgm,
      }
    }));

    const requestSource = this.authRequestInitialService.externalRequestSource$.value;
    const orderingProvider = this.authRequestInitialService.selectedPhysician$.value;
    const servicingProvider = this.authRequestInitialService.selectedServicingProvider$.value;
    const addressAndLocationDate = this.getAddressAndLocationDate();
    const memberId = this.applicationStateService.memberId;

    const body = {
      authorization: {
        authorizationByProduct: { memberId },
        type: { ...type },
        urgency: { ...references?.urgency },
        requestSource,
        referralSource: { ...references?.referralSource },
        referralSourceMemberRepresentative: references?.referralSource?.applicationKey === MEMBER_REPRESENTATIVE ?
          references?.referralSourceMemberRepresentative : null,
        servicingProviderContactFax: contact?.contactFax,
      },
      diagnosis,
      orderingProvider: {
        providerAccountId: orderingProvider?.physicianAccountDTO.physicianAccountId || null,
        providerPgpId: orderingProvider?.physicianPgpDTOList[0].physicianPgpId || null,
      },
      servicingProvider: {
        providerAccountId: servicingProvider?.providerAccountDTO.providerAccountId || null,
        providerPgpId: servicingProvider?.providerPgpDTOList[0].providerPgpId || null
      },
      authorizationServiceLocationByProduct: { ...addressAndLocationDate }
    };

    return this.http.post<string>(`${this.getBaseUrl}/authorizations/draft`, body);
  }

  getAuthorizationsSummary(memberId: string): Observable<IAuthorizationSummaryDTO[]> {
    return this.http.get<IAuthorizationSummaryDTO[]>(
      `${this.getBaseUrl}/members/${memberId}/authorizations/summary`
    );
  }

  getAuthorizationByNumber(authorizationNumber: string): Observable<IAuthorizationDTO> {
    return this.http.get<IAuthorizationDTO>(
      `${this.getBaseUrl}/Authorizations/${authorizationNumber}`
    );
  }

  updateAuthorizationServiceLocation(authorizationByProductId: string, serviceLocation?: IServiceLocation): Observable<IServiceLocationDTO> {
    const addressAndLocationDate = serviceLocation || this.getAddressAndLocationDate();

    return this.http.put<IServiceLocationDTO>(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}/serviceLocation`,
      {
        authorizationByProductId,
        ...addressAndLocationDate,
      }
    );
  }

  getAddressForServiceLocation(authorizationByProductId: string): Observable<IServiceLocationDTO> {
    return this.http.get<IServiceLocationDTO>(
      `${this.getBaseUrl}/authorizations/${authorizationByProductId}/serviceLocation`
    );
  }

  private get getBaseUrl(): string {
    return `${this.apiUrl}/api/tenants/${this.applicationStateService.tenantId}`;
  }

  private getAuthDataForUpdating(authorizationByProductId: string, isExternal: boolean) {
    const { authorization, references, type } = this.authRequestInitialService.authorizationData$.value;
    let data;

    if (references.referralSource?.applicationKey !== MEMBER_REPRESENTATIVE && references.referralSourceMemberRepresentative !== null) {
      references.referralSourceMemberRepresentative = null;
    }

    if (isExternal) {
      data = {
        requestSource: this.authRequestInitialService.externalRequestSource$.value,
        authorizationByProduct: {
          ...authorization?.authorizationByProduct,
          authorizationByProductId,
          memberId: this.applicationStateService.memberId,
        }
      };
    } else {
      data = {
        requestSource: references.requestSource,
        authorizationByProduct: {
          ...authorization?.authorizationByProduct,
          requestReceivedDate: references.requestReceivedDate
        }
      };
    }

    return {
      ...authorization,
      ...data,
      type,
      urgency: references.urgency,
      referralSource: references.referralSource,
      referralSourceMemberRepresentative: references.referralSourceMemberRepresentative,
    };
  }

  getAddressAndLocationDate(): IServiceLocation {
    const { memberAddressDTO } = this.applicationStateService.member$.value;
    const { location, newAddress = {} as IMemberAddress } = this.authRequestInitialService.authorizationData$.value;

    const memberAddress = memberAddressDTO.find((address) => address.addressType === AddressType.Residential) || {} as IMemberAddress;
    const isResidential = location?.addressType.description.toLowerCase() === AddressType.Residential.toLowerCase();

    const { address1, address2, city, state, zipCode } = isResidential ? memberAddress : newAddress;

    return {
      address1,
      address2,
      city,
      state,
      postalCode: zipCode as string,
      addressType: { ...location?.addressType },
      addressLocationType: { ...location?.addressLocationType },
      addressLocationTypeOther: location?.addressLocationType.description.toLowerCase() === OTHER
          ? location?.addressLocationTypeOther
          : null,
    }
  }
}
